import type { FilterResult } from '../../services/filter';
/**
 * Filters the data using Bessel filtering concept.
 *
 * This uses direct multiplication instead of Math.pow.
 * Doing direct multiplication proves to be faster than Math.pow
 *
 * @module
 */
export default class BesselFilter {
  /**
   * Filters the data
   * @function
   * @param {float} frequency
   * @param {float} fc
   * @param {int} filterOrder
   * @returns {FilterResult}
   */
  filterOne(frequency: number, fc: number, filterOrder: number): FilterResult {
    let x = frequency / fc,
      t = 0,
      t0 = 0;
    switch (filterOrder) {
      case 1: {
        t = x * x + 1;
        t0 = 1;
        break;
      }
      case 2: {
        x = x * 1.36165412871613;
        t = x * x * x * x + 3 * x * x + 9;
        t0 = 3;
        break;
      }
      case 3: {
        x = x * 1.75567236868121;
        t = x * x * x * x * x * x + 6 * x * x * x * x + 45 * x * x + 225;
        t0 = 15;
        break;
      }
      case 4: {
        x = x * 2.11391767490422;
        t = x * x * x * x * x * x * x * x + 10 * x * x * x * x * x * x + 135 * x * x * x * x + 1575 * x * x + 11025;
        t0 = 105;
        break;
      }
      case 5: {
        x = x * 2.427410702;
        t =
          x * x * x * x * x * x * x * x * x * x +
          15 * x * x * x * x * x * x * x * x +
          135 * x * x * x * x * x * x +
          6300 * x * x * x * x +
          99225 * x * x +
          893025;
        t0 = 945;
        break;
      }
      case 6: {
        x = x * 2.7;
        t =
          x * x * x * x * x * x * x * x * x * x * x * x +
          21 * x * x * x * x * x * x * x * x * x * x +
          630 * x * x * x * x * x * x * x * x +
          18900 * x * x * x * x * x * x +
          496125 * x * x * x * x +
          9823275 * x * x +
          108056025;
        t0 = 10395;
        break;
      }
      case 7: {
        x = x * 3;
        const z1 = -4 * Math.pow(x, 6) + 450 * Math.pow(x, 4) - 8910 * Math.pow(x, 2) + 19305,
          z2 = Math.pow(x, 6) - 378 * Math.pow(x, 4) + 17325 * Math.pow(x, 2) - 135135,
          z3 = Math.pow(z1, 2) * 49,
          z4 = Math.pow(z2, 2) * Math.pow(x, 2);
        t = z3 + z4;
        t0 = 135135;
        break;
      }
    }

    const numerator = t0,
      denominator = Math.sqrt(t),
      y = numerator / denominator,
      z = (20 * Math.log(y)) / Math.log(10);

    return { filterAmplitude: z, filterAttenuation: y };
  }
}
